import { z } from 'zod';
import { AuditSchema, IdSchema, InstantSchema, VisibilitySchema } from './common.ts';
import { RecordRefSchema, type RecordRef } from './notification.ts';
import type { Visibility } from './common.ts';

/**
 * One comment on one record.
 *
 * Threaded a single level: a comment either starts a thread or replies to
 * one. Deeper nesting is a design that looks tidy in a mock-up and is
 * unreadable on a phone at the side of a road, which is where most of these
 * are read.
 *
 * Job-scoped in the path, like daily logs and to-dos, so a `list` rule can
 * enforce job assignment with one job read. A comment on a company-level
 * file therefore has nowhere to go yet, which is recorded in TASKS.md
 * rather than solved by making every comment company-scoped and losing the
 * assignment check.
 */
export const CommentSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /** What this is about. The `kind` decides the route back to it. */
  recordRef: RecordRefSchema,
  /** Query form of `recordRef`, derived — see `File.linkedKeys`. */
  recordKey: z.string().min(1),
  /** Absent for the first comment of a thread. */
  parentId: IdSchema.optional(),
  body: z.string().trim().min(1).max(5_000),
  /**
   * Who can see it.
   *
   * Inherits the record's and may be narrowed, never widened — an
   * internal-only note on an owner-visible log is the case the whole
   * feature exists for, and the reverse would leak the record itself.
   */
  visibility: VisibilitySchema,
  /** Uids named with an @mention, for the trigger that tells them. */
  mentionedUids: z.array(IdSchema).default([]),
  editedAt: InstantSchema.optional(),
});
export type Comment = z.infer<typeof CommentSchema>;

/** The query form of a record reference. */
export function recordKey(ref: Pick<RecordRef, 'kind' | 'id'>): string {
  return `${ref.kind}:${ref.id}`;
}

/**
 * Whether a comment's audience is within its record's.
 *
 * Narrowing is allowed and widening is not. A comment visible to the owner
 * on a log the owner cannot see would tell them the log exists and quote
 * part of it, which is a leak of the record through its own comments.
 *
 * `private` is always allowed: it is the narrowest audience there is.
 */
export function isNarrowerOrEqual(record: Visibility, comment: Visibility): boolean {
  if (comment.private) return true;
  if (record.private) {
    // A private record's comments can only be private too — anything else
    // would be visible to people who cannot see what it is about.
    return false;
  }
  return (
    (!comment.internal || record.internal) &&
    (!comment.subs || record.subs) &&
    (!comment.owner || record.owner)
  );
}

/**
 * Threads, in the order they are read.
 *
 * Roots oldest first — a conversation is read from the top — and replies
 * under each root in the same order. A reply whose parent is missing is
 * promoted to a root rather than dropped, for the same reason an orphaned
 * folder is shown: a view that silently omits rows loses what people wrote.
 */
export interface CommentThread {
  readonly comment: Comment;
  readonly replies: readonly Comment[];
}

export function threadComments(comments: readonly Comment[]): CommentThread[] {
  const byId = new Set(comments.map((comment) => comment.id));
  const oldestFirst = (a: Comment, b: Comment) =>
    a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);

  const roots = comments
    .filter((comment) => comment.parentId === undefined || !byId.has(comment.parentId))
    .sort(oldestFirst);

  const repliesByParent = new Map<string, Comment[]>();
  for (const comment of comments) {
    const parentId = comment.parentId;
    if (parentId === undefined || !byId.has(parentId)) continue;
    const existing = repliesByParent.get(parentId);
    if (existing) existing.push(comment);
    else repliesByParent.set(parentId, [comment]);
  }

  return roots.map((comment) => ({
    comment,
    // A reply to a reply is flattened under the root it belongs to, because
    // the data model is one level and a deeper chain could only have been
    // written by something that is not this product.
    replies: (repliesByParent.get(comment.id) ?? []).sort(oldestFirst),
  }));
}

/**
 * Uids named in a comment body.
 *
 * Matched against the people who could be named rather than by pattern
 * alone: `@mike` is a display name, not a uid, and inventing a uid from
 * text would mean a mention that notifies nobody and reads as a bug.
 */
export function mentionsIn(
  body: string,
  people: readonly { readonly uid: string; readonly displayName?: string }[],
): string[] {
  const found: string[] = [];
  for (const person of people) {
    const name = person.displayName?.trim();
    if (!name) continue;
    // Word-boundary-ish: "@Mike" must not match inside "@Mikela".
    const pattern = new RegExp(`@${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w])`, 'i');
    if (pattern.test(body)) found.push(person.uid);
  }
  return [...new Set(found)];
}
